/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { SubDepartment } from "types/models/menu"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { mapItemDepartmentToSubDepartment } from "utils/menuV3Mapper"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
  isMenuFilterActive,
} from "utils/menu_helper"

interface UseMenuOverviewSubDepartmentsReturn {
  filteredSubDepartments: SubDepartment[]
  isLoading: boolean
  isFetching: boolean
}

export const useMenuOverviewSubDepartments = (): UseMenuOverviewSubDepartmentsReturn => {
  const enablePartialMenu = useEnablePartialMenu()

  // Get data from Redux
  const {
    overview: {
      subDepartments: reduxSubDepartments,
      department: selectedDepartment,
      sellingTime,
    },
    merchantID,
    merchantGroupID,
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  // Check if filters are active
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Use martItemMenuDisplayV2 query only when:
  // 1. enablePartialMenu is true
  // 2. Filters are active (otherwise V3 components handle their own data)
  const {
    data: martMenuData,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
  } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID || !isFilterActive,
    },
  )

  // Get sub-departments from query or Redux based on enablePartialMenu
  const subDepartments = useMemo(() => {
    if (enablePartialMenu && martMenuData && selectedDepartment) {
      // Get from query: Find the selected department in the mart menu data
      const department = martMenuData.departments?.find(
        dept => dept.itemClassID === selectedDepartment.itemClassID,
      )
      if (department) {
        return department.subDepartments.map(subDept =>
          mapItemDepartmentToSubDepartment(
            subDept,
            department.itemClassID,
            department.itemClassName,
          ),
        )
      }
      return []
    }
    // Get from Redux
    return reduxSubDepartments
  }, [enablePartialMenu, martMenuData, selectedDepartment, reduxSubDepartments])

  // Filter sub-departments by selling time and item without photo
  const filteredSubDepartments = useMemo(() => {
    let result = subDepartments

    // Filter by selling time (matches existing pattern - always filter regardless of Redux/query)
    if (sellingTime) {
      result = result
        .filter(subDepartment =>
          subDepartment.items?.some(
            item => item.sellingTimeID === sellingTime.sellingTimeID,
          ),
        )
        .map(subDepartment => ({
          ...subDepartment,
          items: subDepartment.items?.filter(
            item => item.sellingTimeID === sellingTime.sellingTimeID,
          ),
        }))
    }

    // Filter by item without photo
    if (isItemWithoutPhotoMode(itemFilter)) {
      result = result.filter(subDepartment => {
        return getItemWithoutPhotoCount(subDepartment.items) !== 0
      })
    }

    return result
  }, [subDepartments, itemFilter, sellingTime])

  // Determine loading state: use query loading when partial menu is enabled
  const isLoading =
    enablePartialMenu && (isQueryLoading || (isQueryFetching && !martMenuData))

  return {
    filteredSubDepartments,
    isLoading: !!isLoading,
    isFetching: enablePartialMenu ? isQueryFetching : false,
  }
}
